import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box } from '@mui/material';
import MuiAvatar from '@mui/material/Avatar';
import MuiCircularProgress from '@mui/material/CircularProgress';

import { useCurrentUser } from '~/src/common/user';
import { black20, currentUserZIndex, pinkishOrange, turquoise } from '~/styles';

import { IconProfilePlaceholder } from './icons';

interface CurrentUserAvatarProps {
  width: string | number;
  fontSize?: string;
  placeholderFill?: boolean;
  showGoalRings?: boolean;
  forcedMeditationRingValue?: number;
  forcedClassRingValue?: number;
  showShadowPlaceholder?: boolean;
  className?: string;
}
interface AvatarProps {
  fontSize?: string;
}
interface CircularProgressProps {
  type: 'meditation' | 'class';
  showGoalRings?: boolean;
}

const parseWidth = (width: string | number): string => {
  return typeof width === 'number' ? `${width}px` : width;
};

const BaseMuiAvatar: React.FC<React.ComponentProps<typeof MuiAvatar>> = props => (
  <MuiAvatar {...props} />
);
const Avatar = styled(BaseMuiAvatar)<AvatarProps>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  position: absolute;
  z-index: ${currentUserZIndex.avatar};
  transform: translate(10%, -40%);
  top: 50%;
  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize};
    `};
  text-transform: uppercase;
  margin: auto;
` as React.FC<PropsWithChildren<AvatarProps & React.ComponentProps<typeof MuiAvatar>>>;

const BaseMuiCircularProgress: React.FC<
  React.ComponentProps<typeof MuiCircularProgress>
> = props => <MuiCircularProgress {...props} />;
const CircularProgress = styled(BaseMuiCircularProgress)<CircularProgressProps>`
  position: absolute;
  box-shadow: 0 0 1px 1px ${black20};
  border-radius: 50%;
  ${({ type }) =>
    type === 'meditation'
      ? css`
          color: ${turquoise};
          top: 0;
          left: 0;
          z-index: ${currentUserZIndex.meditation};
        `
      : css`
          color: ${pinkishOrange};
          top: -12%;
          left: -12%;
          z-index: ${currentUserZIndex.class};
          background: white;
        `};
` as React.FC<
  PropsWithChildren<CircularProgressProps & React.ComponentProps<typeof MuiCircularProgress>>
>;

export const CurrentUserAvatar: React.FC<PropsWithChildren<CurrentUserAvatarProps>> = ({
  width,
  fontSize,
  showGoalRings = true,
  forcedMeditationRingValue,
  forcedClassRingValue,
  placeholderFill,
  showShadowPlaceholder,
  ...props
}: CurrentUserAvatarProps) => {
  const { currentUser } = useCurrentUser();

  let meditationRingValue = 0;
  let classRingValue = 0;
  if (currentUser) {
    meditationRingValue =
      currentUser.meditation_goal === 0
        ? 0
        : (currentUser.meditation_count_current_week / currentUser.meditation_goal) * 100;

    classRingValue =
      currentUser.class_goal === 0
        ? 0
        : (currentUser.class_count_current_week / currentUser.class_goal) * 100;

    if (forcedMeditationRingValue) {
      meditationRingValue = (forcedMeditationRingValue / currentUser.meditation_goal) * 100;
    }
    if (forcedClassRingValue) {
      classRingValue = (forcedClassRingValue / currentUser.class_goal) * 100;
    }

    meditationRingValue = meditationRingValue > 100 ? 100 : meditationRingValue;
    classRingValue = classRingValue > 100 ? 100 : classRingValue;
  }

  return (
    currentUser && (
      <Box
        position="relative"
        width={parseWidth(width)}
        height={parseWidth(width)}
        top="-9px"
        left="-9px"
        margin="auto"
        {...props}
      >
        {currentUser?.image ? (
          <Avatar
            data-testid="profile-uploaded-img"
            imgProps={{ role: 'presentation' }}
            fontSize={fontSize}
            src={currentUser.image.processed_url || ''}
          />
        ) : (
          <Avatar
            imgProps={{ role: 'presentation' }}
            fontSize={fontSize}
            children={
              showShadowPlaceholder ? (
                currentUser.first_name ? (
                  currentUser.first_name.charAt(0)
                ) : (
                  currentUser.email.charAt(0)
                )
              ) : (
                <IconProfilePlaceholder
                  height={'100%'}
                  width={'100%'}
                  fill={placeholderFill ? '#bdbdbd' : 'white'}
                />
              )
            }
          />
        )}

        {showGoalRings && (
          <>
            <CircularProgress
              data-testid="user-meditation-progress-ring"
              variant="determinate"
              value={meditationRingValue}
              type="meditation"
              size="120%"
            />
            <CircularProgress
              data-testid="user-class-progress-ring"
              variant="determinate"
              value={classRingValue}
              type="class"
              size="144%"
            />
          </>
        )}
      </Box>
    )
  );
};
